import React from "react";
import { X, Loader2, TriangleAlert } from "lucide-react";

/**
 * Delete confirmation with the two outcomes the admin must choose between.
 *
 * Neither option deletes a project. "Disable" reuses the existing project
 * disable flow, so the projects move to the admin-only "View Disable Projects"
 * list and can be re-enabled at any time.
 */
const DeleteOrganisationModal = ({
  organisation,
  onCancel,
  onConfirm,
  isDeleting,
  error,
}) => {
  if (!organisation) return null;

  const projectCount =
    organisation.projectCount ?? (organisation.projects || []).length;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-50" onClick={onCancel} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[600px] max-w-[calc(100vw-32px)] p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-[42px] h-[42px] rounded-full bg-[#FFF0F0] flex items-center justify-center flex-shrink-0">
              <TriangleAlert size={22} color="#EF4444" strokeWidth={2.2} />
            </div>
            <h2 className="text-[20px] font-semibold text-black">
              Delete Organisation
            </h2>
          </div>
          <button onClick={onCancel} aria-label="Close">
            <X size={24} className="text-black opacity-50" />
          </button>
        </div>

        <p className="text-[14px] text-black mt-4">
          Delete <strong>{organisation.name}</strong>?
        </p>

        {error && (
          <div className="mt-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
            {error}
          </div>
        )}

        {projectCount > 0 ? (
          <>
            <p className="text-[13px] text-gray-500 mt-2">
              This organisation has {projectCount} project
              {projectCount === 1 ? "" : "s"}. Choose what should happen to
              {projectCount === 1 ? " it" : " them"}.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <button
                disabled={isDeleting}
                onClick={() => onConfirm("unlink")}
                className="text-left border border-gray-200 hover:border-[#4253C7] rounded-lg p-4 disabled:opacity-60"
              >
                <p className="text-sm font-medium text-black">
                  Keep projects active
                </p>
                <p className="text-[12px] text-gray-500 mt-1">
                  Projects leave this organisation but stay exactly as they are
                  on the Projects page.
                </p>
              </button>

              <button
                disabled={isDeleting}
                onClick={() => onConfirm("disable")}
                className="text-left border border-gray-200 hover:border-[#D4A72C] rounded-lg p-4 bg-[#FFF8E5] disabled:opacity-60"
              >
                <p className="text-sm font-medium text-[#7D4E00]">
                  Move projects to disabled
                </p>
                <p className="text-[12px] text-[#7D4E00] mt-1">
                  Projects are disabled and hidden from the main list. Nothing
                  is deleted — you can re-enable them from “View Disable
                  Projects”.
                </p>
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-[13px] text-gray-500 mt-2">
              This organisation has no projects. Client contacts stay in
              Settings &gt; Resources.
            </p>
            <div className="mt-6">
              <button
                disabled={isDeleting}
                onClick={() => onConfirm("unlink")}
                className="w-full bg-[#EF4444] text-white h-[40px] rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isDeleting && <Loader2 size={14} className="animate-spin" />}
                Delete Organisation
              </button>
            </div>
          </>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="border border-gray-300 text-gray-600 w-[140px] h-[40px] rounded-lg text-sm disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default DeleteOrganisationModal;
